import React, { useState, useEffect } from 'react';
import { ArrowLeft, GraduationCap, Users, Clock, BookOpen, Award, Target, FileText, CheckCircle, Mail, Globe, Calendar, DollarSign, Loader2, Building } from 'lucide-react';
import { Link } from 'react-router-dom';
import Carousel from '../../components/Carousel';

const Doctorado = () => {
  // Estados para datos dinámicos
  const [programaData, setProgramaData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [datosBancarios, setDatosBancarios] = useState(null);

  // Cargar datos desde JSON
  useEffect(() => {
    const loadProgramaData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`/data/doctorado.json?v=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) {
          throw new Error('Error al cargar los datos del programa');
        }
        const data = await response.json();
        setProgramaData(data);
        setError(null);
      } catch (err) {
        console.error('Error cargando datos de doctorado:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    loadProgramaData();
  }, []);

  // Cargar datos bancarios desde generales.json
  useEffect(() => {
    const fetchDatosBancarios = async () => {
      try {
        const response = await fetch(`/data/generales.json?v=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) throw new Error('Error al cargar datos bancarios');
        const data = await response.json();
        setDatosBancarios(data.datosBancarios);
      } catch (err) {
        setDatosBancarios(null);
      }
    };
    fetchDatosBancarios();
  }, []);

  // Datos dinámicos desde JSON
  const calendario = programaData?.calendario.inscripciones || {
    periodo: 'Cargando...',
    resultados: 'Cargando...',
    inicio: 'Cargando...',
    fin: 'Cargando...'
  };

  const costos = programaData?.costos || {
    inscripcion: 'Cargando...',
    mensualidad: 'Cargando...',
    duracion: 'Cargando...'
  };

  const modalidadData = programaData?.modalidad || {
    tipo: 'Cargando...',
    metodologia: 'Cargando...',
    plataforma: 'Cargando...',
    horario: 'Cargando...'
  };

  const periodoNombre = programaData?.calendario.periodo_nombre || '2025 - 2026';

  const correoContacto = programaData?.generales?.correo || 'inscripcionespostgrados@cenid.org';

  // Información del programa (datos estáticos)
  const informacionGeneral = {
    titulo: "Doctorado en Educación",
    rvoe: "RVOE-BC-D003-M1/19",
    modalidad: modalidadData.tipo,
    duracion: costos.duracion,
    inscripcion: `${costos.inscripcion} semestralmente`,
    mensualidad: costos.mensualidad,
    metodologia: modalidadData.metodologia,
    plataforma: modalidadData.plataforma,
    horario: modalidadData.horario
  };

  // Documentación requerida
  const documentosNacionales = [
    'Acta de nacimiento',
    'Clave Única de Registro de Población (CURP)',
    'Copia de Identificación oficial',
    'Original o copia certificada del Certificado Total del grado inmediato anterior',
    'Cédula profesional y/o Título de Grado inmediato anterior'
  ];

  const documentosExtranjeros = [
    'Acta de nacimiento apostillada o legalizada en el país de origen',
    'Copia de Identificación oficial vigente',
    'Original o copia apostillada del Título de Grado del nivel inmediato anterior',
    'Certificado de estudios apostillado o legalizado'
  ];

  // Líneas de investigación específicas para Doctorado
  const lineasInvestigacion = [
    'Gerencia de proyectos',
    'Gerencia estratégica',
    'Política educativa, sujetos sociales, gestión y desarrollo institucional',
    'Evaluación institucional',
    'Administración y gestión educativa',
    'Liderazgo y cambio en el sistema educativo',
    'Educación y políticas para la sostenibilidad'
  ];

  // Mostrar estado de carga
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#0b742f] animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Cargando información del programa...</p>
        </div>
      </div>
    );
  }

  // Mostrar error si falla la carga
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center px-4">
          <p className="text-red-600 font-semibold text-xl mb-2">Error al cargar la información</p>
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://recursos.cenid.org/img_cenid/servicios/img_doctorado3.jpg"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
              <GraduationCap className="w-12 h-12" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-2">
                {informacionGeneral.titulo}
              </h1>
              <p className="text-xl text-green-100 flex items-center gap-2">
                <Award className="w-5 h-5" />
                {informacionGeneral.rvoe}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Quick Info Cards */}
        <div className="grid md:grid-cols-3 gap-6 -mt-12 mb-16 relative z-10">
          <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-[#0b742f] hover:scale-105 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-[#0b742f]/10 p-3 rounded-lg">
                <Clock className="w-6 h-6 text-[#0b742f]" />
              </div>
              <h3 className="font-bold text-gray-900">Duración</h3>
            </div>
            <p className="text-2xl font-bold text-[#0b742f]">{informacionGeneral.duracion}</p>
          </div>

          <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-[#e87729] hover:scale-105 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-[#e87729]/10 p-3 rounded-lg">
                <Globe className="w-6 h-6 text-[#e87729]" />
              </div>
              <h3 className="font-bold text-gray-900">Modalidad</h3>
            </div>
            <p className="text-2xl font-bold text-[#e87729]">{informacionGeneral.modalidad}</p>
          </div>

          <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-[#0b742f] hover:scale-105 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-[#0b742f]/10 p-3 rounded-lg">
                <DollarSign className="w-6 h-6 text-[#0b742f]" />
              </div>
              <h3 className="font-bold text-gray-900">Mensualidad</h3>
            </div>
            <p className="text-2xl font-bold text-[#0b742f]">{informacionGeneral.mensualidad}</p>
          </div>
        </div>

        {/* Modalidad e Información */}
        <section className="mb-16">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Modalidad */}
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg p-8 border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <Globe className="w-8 h-8 text-[#0b742f]" />
                <h3 className="text-2xl font-bold text-gray-900">Modalidad</h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-600">Tipo:</span>
                  <span className="font-semibold text-[#0b742f]">{informacionGeneral.modalidad}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-600">Metodología:</span>
                  <span className="font-semibold text-gray-900">{informacionGeneral.metodologia}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-600">Plataforma:</span>
                  <span className="font-semibold text-gray-900">{informacionGeneral.plataforma}</span>
                </div>
                <div className="space-y-2">
                  <span className="font-semibold text-gray-900 text-sm leading-relaxed block">{informacionGeneral.horario}</span>
                </div>
              </div>
            </div>

            {/* Costos */}
            <div className="bg-gradient-to-br from-[#e87729] to-[#d66820] text-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center gap-3 mb-6">
                <DollarSign className="w-8 h-8" />
                <h3 className="text-2xl font-bold">Inversión</h3>
              </div>
              <div className="space-y-6">
                <div>
                  <p className="text-orange-100 text-sm mb-1">Inscripción semestral</p>
                  <p className="text-4xl font-bold">{informacionGeneral.inscripcion}</p>
                </div>
                <div>
                  <p className="text-orange-100 text-sm mb-1">Colegiatura mensual</p>
                  <p className="text-4xl font-bold">{informacionGeneral.mensualidad}</p>
                </div>
                <div className="bg-white/20 rounded-lg p-4 mt-4">
                  <p className="text-sm">✨ Modalidad de Acreditación por competencias disponible</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Inscripciones */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-l-4 border-[#0b742f]">
            <div className="flex items-center gap-3 mb-8">
              <Calendar className="w-8 h-8 text-[#0b742f]" />
              <h2 className="text-3xl font-bold text-gray-900">Calendario {periodoNombre}</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-green-50 rounded-lg p-6">
                <p className="text-sm text-gray-600 mb-2">Periodo de inscripciones</p>
                <p className="text-lg font-bold text-[#0b742f]">{calendario.periodo}</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-6">
                <p className="text-sm text-gray-600 mb-2">Publicación de resultados</p>
                <p className="text-lg font-bold text-[#e87729]">{calendario.resultados}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-6">
                <p className="text-sm text-gray-600 mb-2">Inicio del ciclo escolar</p>
                <p className="text-lg font-bold text-[#0b742f]">{calendario.inicio}</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-6">
                <p className="text-sm text-gray-600 mb-2">Fin del periodo escolar</p>
                <p className="text-lg font-bold text-[#e87729]">{calendario.fin}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Objetivo General */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-[#0b742f] to-[#085a24] text-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <Target className="w-10 h-10 text-[#e87729]" />
              <h2 className="text-3xl font-bold">Objetivo General</h2>
            </div>
            <p className="text-lg leading-relaxed text-white/95">
              Formar investigadores especialistas de alto nivel en el campo de la Educación,
              capaces de generar y aplicar el conocimiento en las áreas de la educación,
              con los conocimientos, habilidades y actitudes que le permitan actuar de manera
              directa sobre problemas que surjan en las organizaciones educativas, de los actores
              sociales y de las políticas locales, regionales y globales en procesos educativos,
              capaces de apropiarse de enfoques teóricos que les permitan comprender, analizar y
              ejecutar procesos de la educación, desempeñándose siempre con un alto sentido ético
              y compromiso social.
            </p>
          </div>
        </section>

        {/* Objetivos Específicos */}
        <section className="mb-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Objetivos Específicos</h2>
            <div className="h-1 w-24 bg-[#e87729] rounded mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 hover:border-[#0b742f]">
              <div className="bg-[#0b742f]/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <span className="text-[#0b742f] font-bold text-xl">1</span>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Cooperar a la formación de recursos humanos en Educación desde un enfoque
                humanístico, responsable y de compromiso con los valores inherentes al
                quehacer educativo.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 hover:border-[#0b742f]">
              <div className="bg-[#0b742f]/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <span className="text-[#0b742f] font-bold text-xl">2</span>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Incentivar y ayudar a la formación de investigadores en el campo de la
                Educación con sólidas bases teóricas, epistemológicas y metodológicas y
                con competencias que les permitan diseñar y realizar proyectos de investigación
                que aporten al desarrollo del campo de la educación.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 hover:border-[#0b742f]">
              <div className="bg-[#0b742f]/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <span className="text-[#0b742f] font-bold text-xl">3</span>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Promover el desarrollo, difusión y gestión de conocimiento original en el
                ámbito de la educación que contribuya al intercambio con la comunidad de
                investigadores a nivel estatal, nacional e internacional.
              </p>
            </div>
          </div>
        </section>

        {/* Perfil de Egreso */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-8">
              <div className="bg-[#e87729] p-3 rounded-lg">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Perfil de Egreso</h2>
            </div>
            <p className="text-lg text-gray-700 mb-6">
              Los egresados del Doctorado en Educación contarán con una sólida formación
              teórica y metodológica, con los conocimientos, destrezas y habilidades que
              les permitirán ser capaces de:
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-4 bg-green-50 rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-[#0b742f] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">
                  Contar con bases actuales y llevar acabo diagnósticos, toma de decisiones
                  en materia de promoción, instrumentación, operación y evaluación.
                </p>
              </div>
              <div className="flex items-start gap-4 bg-green-50 rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-[#0b742f] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">
                  Diseñar, operar y evaluar proyectos de investigación de alto impacto para
                  la resolución de problemáticas reales a nivel local, estatal o nacional
                  aplicando metodologías cualitativas y cuantitativas, manejando información
                  científica en general, así como aquella que proviene de fuentes especializadas.
                </p>
              </div>
              <div className="flex items-start gap-4 bg-green-50 rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-[#0b742f] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">
                  Liderazgo en su ámbito de influencia que contribuya a promover una sociedad
                  más equitativa y responsable conforme a los valores promovidos por el CENID
                  para hacer frente a los retos de la sociedad contemporánea.
                </p>
              </div>
              <div className="flex items-start gap-4 bg-green-50 rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-[#0b742f] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">
                  Capaz de crear grupos de trabajo y coordinación de equipos multidisciplinarios
                  e interdisciplinarios, con iniciativa y apertura al cambio respetando la
                  pluralidad teórica e ideológica para favorecer el establecimiento de redes
                  a nivel nacional e internacional utilizando las nuevas tecnologías de la
                  información y la comunicación (TIC).
                </p>
              </div>
              <div className="flex items-start gap-4 bg-green-50 rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-[#0b742f] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">
                  Analizar las políticas educativas con una actitud objetiva para la
                  comprensión del desarrollo social.
                </p>
              </div>
              <div className="flex items-start gap-4 bg-green-50 rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-[#0b742f] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">
                  Desarrollar investigaciones en instituciones de educación y/o dentro de
                  las comunidades sociales con habilidad para comunicar los resultados
                  proponiendo alternativas a las problemáticas estudiadas.
                </p>
              </div>
              <div className="flex items-start gap-4 bg-green-50 rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-[#0b742f] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">
                  Asumir una posición crítica, producto de una reflexión sistemática, sobre
                  la sociedad actual y la relación centro región, para problematizar y construir
                  conocimiento en el campo de los estudios regionales, así como para entablar
                  debates y aportar elementos innovadores, tanto teóricos como metodológicos.
                </p>
              </div>
              <div className="flex items-start gap-4 bg-green-50 rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-[#0b742f] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">
                  Reconocimiento por la diversidad cultural y por las posturas intelectuales.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Acreditación por Competencias */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-xl p-8 md:p-12 border border-blue-100">
            <div className="flex items-center gap-3 mb-8">
              <div className="bg-blue-500 p-3 rounded-lg">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Acreditación por Competencias</h2>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <p className="text-lg text-gray-700 mb-6">
                El doctorado ofrece una modalidad innovadora de acreditación por competencias que permite a los
                estudiantes avanzar más rápidamente en su formación.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Acreditación de competencias, aquellos que se realizan a solicitud del alumno
                cuando considere que por razones de experiencia laboral o de estudios previos
                ha logrado una formación en ese campo y está en condiciones de demostrar los
                conocimientos necesarios para acreditar una materia. Esta opción te permite
                avanzar más rápidamente en la ruta de formación propuesta para el programa de
                postgrado que estás cursando, por lo que se te recomienda que revises los
                contenidos de las materias que integran tu plan de estudios, para identificar
                aquellas en las que tengas posibilidades de presentarlo.
              </p>
            </div>
          </div>
        </section>

        {/* Líneas de Investigación */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl shadow-xl p-8 md:p-12 border border-indigo-100">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Líneas y Proyectos de Investigación</h2>
              <div className="h-1 w-24 bg-[#e87729] rounded mx-auto"></div>
            </div>

            {/* Descripción introductoria */}
            <div className="bg-white rounded-xl p-6 shadow-md mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                En su proceso formativo cada estudiante está asociado a una línea de investigación y trabaja
                un problema contenido en una de las áreas temáticas de las que se ocupa este programa.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                En el cruce de líneas de investigación con las áreas temáticas se articulan los intereses de
                investigación del estudiante, los proyectos de investigación desarrollados por los académicos
                y las necesidades sociales de la entidad.
              </p>
            </div>

            {/* Título de las líneas */}
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                El Programa ofrece siete líneas de investigación:
              </h3>
            </div>

            {/* Grid de líneas de investigación */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {lineasInvestigacion.map((linea, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 p-4 border border-gray-100 hover:border-[#0b742f] group"
                >
                  <div className="flex items-start gap-3">
                    <div className="bg-[#0b742f]/10 group-hover:bg-[#0b742f] p-2 rounded-lg transition-colors duration-300 flex-shrink-0">
                      <span className="text-[#0b742f] group-hover:text-white font-bold text-sm transition-colors duration-300">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed font-medium">{linea}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Información adicional */}
            <div className="mt-8 bg-indigo-100 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <div className="bg-indigo-500 p-2 rounded-lg flex-shrink-0">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-indigo-900 mb-2">Desarrollo del Proyecto de Investigación</h4>
                  <p className="text-indigo-800 text-sm">
                    Cada línea de investigación está respaldada por académicos especializados que guían a los
                    estudiantes en el desarrollo de proyectos innovadores que contribuyen al avance del conocimiento
                    en el campo educativo y responden a las necesidades específicas de la región.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mapa Curricular */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-8">
              <FileText className="w-8 h-8 text-[#e87729]" />
              <h2 className="text-3xl font-bold text-gray-900">Mapa Curricular</h2>
            </div>

            {/* Título del programa */}
            <div className="text-center mb-8">
              <h3 className="text-xl font-bold text-[#0b742f] mb-2">
                Doctorado en Educación
              </h3>
              <p className="text-gray-600">Tres semestrales • Modalidad: virtual</p>
              <p className="text-sm text-gray-500 mt-1">*Modalidad: Acreditación por competencias</p>
            </div>

            {/* Grid de semestres */}
            <div className="grid md:grid-cols-3 gap-6">
              {/* PRIMER SEMESTRE */}
              <div className="bg-white rounded-xl shadow-lg border-t-4 border-[#0b742f] p-6">
                <div className="bg-[#0b742f] text-white text-center py-3 -mx-6 -mt-6 mb-6 rounded-t-lg">
                  <h4 className="font-bold text-lg">Primer semestre</h4>
                  <p className="text-sm text-green-100">33 Créditos</p>
                </div>
                <div className="space-y-3">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200 hover:shadow-md transition-shadow">
                    <p className="font-semibold text-gray-800 text-xs mb-1">Fundamentos de los sistemas educativos</p>
                    <p className="text-xs text-gray-600">DED101</p>
                    <p className="text-xs text-gray-500">HI 80 • HD 32 • 7 créditos</p>
                  </div>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200 hover:shadow-md transition-shadow">
                    <p className="font-semibold text-gray-800 text-xs mb-1">Enfoque social en educación</p>
                    <p className="text-xs text-gray-600">DED102</p>
                    <p className="text-xs text-gray-500">HI 80 • HD 32 • 7 créditos</p>
                  </div>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200 hover:shadow-md transition-shadow">
                    <p className="font-semibold text-gray-800 text-xs mb-1">Análisis de las instituciones educativas</p>
                    <p className="text-xs text-gray-600">DED202</p>
                    <p className="text-xs text-gray-500">HI 80 • HD 32 • 7 créditos</p>
                  </div>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200 hover:shadow-md transition-shadow">
                    <p className="font-semibold text-gray-800 text-xs mb-1">Fundamentos de la Administración y la Gestión Educativa</p>
                    <p className="text-xs text-gray-600">DED201</p>
                    <p className="text-xs text-gray-500">HI 80 • HD 32 • 7 créditos</p>
                  </div>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200 hover:shadow-md transition-shadow">
                    <p className="font-semibold text-gray-800 text-xs mb-1">Tutoría I</p>
                    <p className="text-xs text-gray-600">DED103</p>
                    <p className="text-xs text-gray-500">HI 48 • HD 32 • 5 créditos</p>
                  </div>
                </div>
              </div>

              {/* SEGUNDO SEMESTRE */}
              <div className="bg-white rounded-xl shadow-lg border-t-4 border-blue-600 p-6">
                <div className="bg-blue-600 text-white text-center py-3 -mx-6 -mt-6 mb-6 rounded-t-lg">
                  <h4 className="font-bold text-lg">Segundo semestre</h4>
                  <p className="text-sm text-blue-100">31 Créditos</p>
                </div>
                <div className="space-y-3">
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-3 rounded-lg border border-blue-200 hover:shadow-md transition-shadow">
                    <p className="font-semibold text-gray-800 text-xs mb-1">Taller de investigación I</p>
                    <p className="text-xs text-gray-600">DED203</p>
                    <p className="text-xs text-gray-500">HI 48 • HD 32 • 5 créditos</p>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-3 rounded-lg border border-blue-200 hover:shadow-md transition-shadow">
                    <p className="font-semibold text-gray-800 text-xs mb-1">Diseño, implementación y evaluación de políticas sociales</p>
                    <p className="text-xs text-gray-600">DED301</p>
                    <p className="text-xs text-gray-500">HI 80 • HD 32 • 7 créditos</p>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-3 rounded-lg border border-blue-200 hover:shadow-md transition-shadow">
                    <p className="font-semibold text-gray-800 text-xs mb-1">Aspectos contemporáneos de la enseñanza y el aprendizaje</p>
                    <p className="text-xs text-gray-600">DED302</p>
                    <p className="text-xs text-gray-500">HI 80 • HD 32 • 7 créditos</p>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-3 rounded-lg border border-blue-200 hover:shadow-md transition-shadow">
                    <p className="font-semibold text-gray-800 text-xs mb-1">OPTATIVA</p>
                    <p className="text-xs text-gray-600">ODE101</p>
                    <p className="text-xs text-gray-500">Seriación DED201</p>
                    <p className="text-xs text-gray-500">HI 80 • HD 32 • 7 créditos</p>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-3 rounded-lg border border-blue-200 hover:shadow-md transition-shadow">
                    <p className="font-semibold text-gray-800 text-xs mb-1">Tutoría II</p>
                    <p className="text-xs text-gray-600">DED303</p>
                    <p className="text-xs text-gray-500">Seriación DED103</p>
                    <p className="text-xs text-gray-500">HI 48 • HD 32 • 5 créditos</p>
                  </div>
                </div>
              </div>

              {/* TERCER SEMESTRE */}
              <div className="bg-white rounded-xl shadow-lg border-t-4 border-[#e87729] p-6">
                <div className="bg-[#e87729] text-white text-center py-3 -mx-6 -mt-6 mb-6 rounded-t-lg">
                  <h4 className="font-bold text-lg">Tercer semestre</h4>
                  <p className="text-sm text-orange-100">29 Créditos</p>
                </div>
                <div className="space-y-3">
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-3 rounded-lg border border-orange-200 hover:shadow-md transition-shadow">
                    <p className="font-semibold text-gray-800 text-xs mb-1">Taller de investigación II</p>
                    <p className="text-xs text-gray-600">DED402</p>
                    <p className="text-xs text-gray-500">Seriación DED203</p>
                    <p className="text-xs text-gray-500">HI 48 • HD 32 • 5 créditos</p>
                  </div>
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-3 rounded-lg border border-orange-200 hover:shadow-md transition-shadow">
                    <p className="font-semibold text-gray-800 text-xs mb-1">Planeación y Evaluación Educativa</p>
                    <p className="text-xs text-gray-600">DED401</p>
                    <p className="text-xs text-gray-500">HI 80 • HD 32 • 7 créditos</p>
                  </div>
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-3 rounded-lg border border-orange-200 hover:shadow-md transition-shadow">
                    <p className="font-semibold text-gray-800 text-xs mb-1">OPTATIVA</p>
                    <p className="text-xs text-gray-600">ODE103</p>
                    <p className="text-xs text-gray-500">Seriación DED202</p>
                    <p className="text-xs text-gray-500">HI 80 • HD 32 • 7 créditos</p>
                  </div>
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-3 rounded-lg border border-orange-200 hover:shadow-md transition-shadow">
                    <p className="font-semibold text-gray-800 text-xs mb-1">Innovación y Cambio Organizacional</p>
                    <p className="text-xs text-gray-600">DED501</p>
                    <p className="text-xs text-gray-500">HI 80 • HD 32 • 7 créditos</p>
                  </div>
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-3 rounded-lg border border-orange-200 hover:shadow-md transition-shadow">
                    <p className="font-semibold text-gray-800 text-xs mb-1">Redacción de tesis I</p>
                    <p className="text-xs text-gray-600">DED502</p>
                    <p className="text-xs text-gray-500">Seriación DED402</p>
                    <p className="text-xs text-gray-500">HI 48 • HD 32 • 5 créditos</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Nota adicional */}
            <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
              <p className="text-sm text-blue-900">
                <strong>Nota:</strong> El programa de doctorado se cursará en tres semestres, cada semestre contará con 5 asignaturas
                y se realizarán exámenes de acreditación por competencias.
              </p>
            </div>

            {/* Resumen de créditos */}
            <div className="mt-8 bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white rounded-xl p-6">
              <div className="grid md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-3xl font-bold">93</p>
                  <p className="text-sm text-green-100">Créditos Totales</p>
                </div>
                <div>
                  <p className="text-3xl font-bold">15</p>
                  <p className="text-sm text-green-100">Asignaturas</p>
                </div>
                <div>
                  <p className="text-3xl font-bold">3</p>
                  <p className="text-sm text-green-100">Semestres</p>
                </div>
                <div>
                  <p className="text-3xl font-bold">1.5</p>
                  <p className="text-sm text-green-100">Años</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Temas a Considerar */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl shadow-xl p-8 md:p-12 border border-amber-100">
            <div className="flex items-center gap-3 mb-8">
              <div className="bg-amber-500 p-3 rounded-lg">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Temas a Considerar</h2>
            </div>
            <p className="text-lg text-gray-700 mb-8 text-center">
              Explora información detallada sobre todos los aspectos importantes del programa
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Antecedentes */}
              <Link
                to="/postgrados/antecedentes"
                className="group bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-blue-500 hover:-translate-y-1"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-500 group-hover:bg-blue-600 p-3 rounded-lg transition-colors duration-300">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                    Antecedentes
                  </h3>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Historia, fundamentos y desarrollo del programa de doctorado
                </p>
                <div className="mt-4 flex items-center text-blue-600 font-semibold text-sm group-hover:gap-2 transition-all duration-300">
                  Conocer más
                  <ArrowLeft className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300 rotate-180" />
                </div>
              </Link>

              {/* Modelo Académico */}
              <Link
                to="/postgrados/modelo-academico"
                className="group bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-green-500 hover:-translate-y-1"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-green-500 group-hover:bg-green-600 p-3 rounded-lg transition-colors duration-300">
                    <BookOpen className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-green-600 transition-colors duration-300">
                    Modelo Académico
                  </h3>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Estructura pedagógica y metodología del programa educativo
                </p>
                <div className="mt-4 flex items-center text-green-600 font-semibold text-sm group-hover:gap-2 transition-all duration-300">
                  Conocer más
                  <ArrowLeft className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300 rotate-180" />
                </div>
              </Link>

              {/* Estructura Orgánica */}
              <Link
                to="/postgrados/estructura-organica"
                className="group bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-purple-500 hover:-translate-y-1"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-purple-500 group-hover:bg-purple-600 p-3 rounded-lg transition-colors duration-300">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-purple-600 transition-colors duration-300">
                    Estructura Orgánica
                  </h3>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Organización académica y administrativa del programa
                </p>
                <div className="mt-4 flex items-center text-purple-600 font-semibold text-sm group-hover:gap-2 transition-all duration-300">
                  Ver organigrama
                  <ArrowLeft className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300 rotate-180" />
                </div>
              </Link>

              {/* Normatividad */}
              <Link
                to="/postgrados/normatividad"
                className="group bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-red-500 hover:-translate-y-1"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-red-500 group-hover:bg-red-600 p-3 rounded-lg transition-colors duration-300">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-red-600 transition-colors duration-300">
                    Normatividad
                  </h3>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Marco legal, reglamentos y políticas institucionales
                </p>
                <div className="mt-4 flex items-center text-red-600 font-semibold text-sm group-hover:gap-2 transition-all duration-300">
                  Ver documentos
                  <ArrowLeft className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300 rotate-180" />
                </div>
              </Link>

              {/* Aspirantes */}
              <Link
                to="/postgrados/aspirantes"
                className="group bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-indigo-500 hover:-translate-y-1"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-indigo-500 group-hover:bg-indigo-600 p-3 rounded-lg transition-colors duration-300">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-indigo-600 transition-colors duration-300">
                    Aspirantes
                  </h3>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Requisitos, proceso de admisión y perfil del aspirante
                </p>
                <div className="mt-4 flex items-center text-indigo-600 font-semibold text-sm group-hover:gap-2 transition-all duration-300">
                  Ver requisitos
                  <ArrowLeft className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300 rotate-180" />
                </div>
              </Link>

              {/* Quiénes Somos */}
              <Link
                to="/postgrados/quienes-somos"
                className="group bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-teal-500 hover:-translate-y-1"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-teal-500 group-hover:bg-teal-600 p-3 rounded-lg transition-colors duration-300">
                    <Globe className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-teal-600 transition-colors duration-300">
                    Quiénes Somos
                  </h3>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Misión, visión, valores y filosofía institucional
                </p>
                <div className="mt-4 flex items-center text-teal-600 font-semibold text-sm group-hover:gap-2 transition-all duration-300">
                  Conocer más
                  <ArrowLeft className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300 rotate-180" />
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* Documentación Requerida */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-xl p-8 md:p-12 border border-blue-100">
            <div className="flex items-center gap-3 mb-8">
              <div className="bg-blue-500 p-3 rounded-lg">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Documentación Requerida</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Estudiantes Mexicanos */}
              <div className="bg-white rounded-xl p-8 shadow-lg border border-green-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-[#0b742f] p-3 rounded-lg">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0b742f]">Alumnos Nacionales</h3>
                </div>
                <p className="text-gray-700 mb-6">
                  Documentación requerida en original y dos fotocopias:
                </p>
                <ul className="space-y-3">
                  {documentosNacionales.map((doc, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#0b742f] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{doc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Estudiantes Extranjeros */}
              <div className="bg-white rounded-xl p-8 shadow-lg border border-blue-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-blue-500 p-3 rounded-lg">
                    <Globe className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-blue-600">Alumnos Extranjeros</h3>
                </div>
                <p className="text-gray-700 mb-6">
                  Documentación apostillada o legalizada:
                </p>
                <ul className="space-y-3">
                  {documentosExtranjeros.map((doc, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{doc}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Cuenta Bancaria */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl shadow-lg p-8 border-l-4 border-[#0b742f]">
            <div className="flex items-center gap-3 mb-6">
              <Building className="w-8 h-8 text-[#0b742f]" />
              <h3 className="text-2xl font-bold text-gray-900">Datos Bancarios para Pagos</h3>
            </div>
            {datosBancarios ? (
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Beneficiario</p>
                    <p className="text-lg font-semibold text-gray-900">{datosBancarios.titular}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Banco</p>
                    <p className="text-lg font-semibold text-gray-900">{datosBancarios.banco}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Cuenta</p>
                    <p className="text-lg font-semibold text-gray-900 font-mono">{datosBancarios.numeroCuenta}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">CLABE Interbancaria</p>
                    <p className="text-lg font-semibold text-gray-900 font-mono">{datosBancarios.clabe}</p>
                  </div>
                  {datosBancarios.sucursal && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Sucursal</p>
                      <p className="text-lg font-semibold text-gray-900">{datosBancarios.sucursal}</p>
                    </div>
                  )}
                  {datosBancarios.swift && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">SWIFT</p>
                      <p className="text-lg font-semibold text-gray-900 font-mono">{datosBancarios.swift}</p>
                    </div>
                  )}
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                    <p className="text-sm text-blue-900">
                      <strong>Importante:</strong> Por favor, envía tu comprobante de pago al correo{' '}
                      <a href={`mailto:${programaData?.generales?.correo || 'info@cenid.org'}`} className="text-blue-600 hover:underline font-semibold">
                        {programaData?.generales?.correo || 'info@cenid.org'}
                      </a>
                      {' '}para confirmar tu inscripción.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin w-6 h-6 text-gray-400" />
                <span className="ml-2 text-gray-500">Cargando datos bancarios...</span>
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section>
          <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white rounded-2xl shadow-2xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">¿Listo para dar el siguiente paso?</h2>
              <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
                Envía tu solicitud de inscripción junto con tu documentación digitalizada
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Contacto de Inscripciones */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="font-bold text-xl mb-4 flex items-center gap-2">
                  <Mail className="w-6 h-6 text-[#e87729]" />
                  Información e Inscripciones
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-green-200" />
                    <span>{correoContacto}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-5 h-5 flex items-center justify-center text-green-200 font-bold">📞</span>
                    <span>(33) 1061-8187</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-5 h-5 flex items-center justify-center text-green-200 font-bold">📍</span>
                    <span className="text-sm">Jalisco, México</span>
                  </div>
                </div>
              </div>

              {/* Enlaces Útiles */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="font-bold text-xl mb-4 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-[#e87729]" />
                  Documentos Importantes
                </h3>
                <div className="space-y-3">
                  <a
                    href="https://www.cenid.org/documentos/reglamento_titulacion.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-green-100 hover:text-white transition-colors duration-300"
                  >
                    <FileText className="w-4 h-4" />
                    <span className="text-sm">Reglamento de Titulación</span>
                  </a>
                  <a
                    href="https://www.cenid.org/postgrados/doctorado"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-green-100 hover:text-white transition-colors duration-300"
                  >
                    <Globe className="w-4 h-4" />
                    <span className="text-sm">Página oficial del programa</span>
                  </a>
                  <div className="flex items-center gap-2 text-green-100">
                    <Award className="w-4 h-4" />
                    <span className="text-sm">RVOE: BC-D005-M1/18</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href={`mailto:${correoContacto}?subject=Solicitud%20de%20información%20-%20Doctorado%20en%20Curriculum&body=Hola,%20me%20interesa%20obtener%20más%20información%20sobre%20el%20Doctorado%20en%20Curriculum,%20Innovación%20y%20Diagnóstico%20de%20la%20Educación.`}
                className="bg-[#e87729] hover:bg-[#d66820] text-white font-semibold px-8 py-4 rounded-lg transition-all duration-300 flex items-center gap-2 hover:scale-105 shadow-lg"
              >
                <Mail className="w-5 h-5" />
                Solicitar Información
              </a>
              <a
                href="https://wa.me/523310618187?text=Hola,%20me%20interesa%20obtener%20información%20sobre%20el%20Doctorado%20en%20Curriculum,%20Innovación%20y%20Diagnóstico%20de%20la%20Educación"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-4 rounded-lg transition-all duration-300 flex items-center gap-2 hover:scale-105 shadow-lg"
              >
                <span className="text-lg">💬</span>
                WhatsApp
              </a>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default Doctorado;